import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext"; // Ensure the user is authenticated
import { useSocket } from "./SocketContext"; // Get the socket instance from SocketContext
import {
  showSystemOnline,
  showSystemOffline,
  showCheckReminder,
  showCheckMissed,
  showInvalidHeadbox,
  showNeuroworksClosed,
  showCustomToast,
} from "../CustomToast";

interface System {
  id: number;
  system_name: string;
  patient_name: string;
  status: string;
  battery_lvl: number;
  charge_status: boolean;
  screenshot?: any;
  claimedby: string | null;
  last_checked: number | null;
  note: string | null;
  ping?: string;
  network_type?: string;
  is_live_view?: string;
  last_seen_at?: string | number | null;
  ss_progress?: number;
  remote_session?: string; // "teamviewer" | "gotomypc" | ""
}

interface SystemsContextType {
  systems: System[];
  setSystems: React.Dispatch<React.SetStateAction<System[]>>;
}

const SystemsContext = createContext<SystemsContextType | undefined>(undefined);

// Create the provider component
export const SystemsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [systems, setSystems] = useState<System[]>([]); // Global state for systems
  const auth = useAuth(); // Get auth context to check authentication
  const { socket } = useSocket(); // Get socket instance from context

  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.sub && socket) {
      socket.on("systems", (data: System[]) => {
        setSystems(data); // Update global systems state
      });

      socket.on("system_online", (system: System) => {
        setSystems((prevSystems) => {
          const updatedSystems = prevSystems.map((s) =>
            s.id === system.id ? system : s,
          );
          // Trigger the toast notification
          showSystemOnline(system.system_name);
          // Check if the system was not found in the previous state, add it if necessary
          const systemExists = prevSystems.some((s) => s.id === system.id);
          if (systemExists) {
            return prevSystems.map((s) => (s.id === system.id ? system : s));
          }
          // Add the new system to the list
          return [...prevSystems, system];
        });
      });

      socket.on("system_offline", (system: System) => {
        setSystems((prevSystems) => {
          return prevSystems.map((s) => (s.id === system.id ? system : s));
        });
        // Trigger the toast notification
        showSystemOffline(system.system_name);
      });

      socket.on("system_change", (system: System) => {
        setSystems((prevSystems) => {
          const prevSystem = prevSystems.find((s) => s.id === system.id);

          if (
            system.status === "invalidHeadbox" &&
            prevSystem?.status !== "invalidHeadbox"
          ) {
            showInvalidHeadbox(system.system_name);
          }

          // Only toast when a study ACTIVELY ENDS (was "online", now isn't).
          // Systems now start in "neuroworksClosed" before a study is loaded,
          // so the old "any transition to neuroworksClosed" check would spam
          // a toast for every system on every reconnect.
          if (
            system.status === "neuroworksClosed" &&
            prevSystem?.status === "online"
          ) {
            showNeuroworksClosed(system.system_name);
          }

          if (
            prevSystem?.status === "invalidHeadbox" &&
            system.status === "online"
          ) {
            showSystemOnline(system.system_name);
          }

          if (
            prevSystem?.status === "neuroworksClosed" &&
            system.status === "online"
          ) {
            showSystemOnline(system.system_name);
          }

          return prevSystems.map((s) =>
            s.id === system.id ? { ...s, ...system } : s,
          );
        });
      });

      socket.on("ping_update", ({ id, ping }: { id: number; ping: number }) => {
        setSystems((prevSystems) =>
          prevSystems.map((s) =>
            Number(s.id) === Number(id) ? { ...s, ping: String(ping) } : s,
          ),
        );
      });

      socket.on(
        "ss_progress",
        ({ systemId, pct }: { systemId: number; pct: number }) => {
          setSystems((prev) =>
            prev.map((s) =>
              Number(s.id) === Number(systemId)
                ? { ...s, ss_progress: pct }
                : s,
            ),
          );
        },
      );

      socket.on("ss_failed", ({ systemId }: { systemId: number }) => {
        setSystems((prev) =>
          prev.map((s) => {
            if (Number(s.id) !== Number(systemId)) return s;
            showCustomToast(
              "Screenshot Failed",
              `${s.system_name || "System"} could not upload the screenshot. Try running the check again.`,
              "error",
            );
            return { ...s, ss_progress: undefined };
          }),
        );
      });

      socket.on("system_removed", ({ id }: { id: number }) => {
        setSystems((prevSystems) => prevSystems.filter((s) => s.id !== id));
      });

      socket.on("claim_failed", ({ reason }: { reason: string }) => {
        showCustomToast("Claim Failed", reason || "Could not claim system.", "error");
      });

      // Non-claim actions the backend refused (approve/deny/unclaim on a
      // system whose claim was lost, etc.). Backend previously dropped
      // these silently, which left operators guessing why a click did
      // nothing.
      socket.on(
        "system_action_failed",
        ({ reason }: { systemId?: number; reason: string }) => {
          showCustomToast(
            "Action Failed",
            reason || "The action could not be completed.",
            "error",
          );
        },
      );

      socket.on("check_reminder", (data: any) => {
        showCheckReminder(data.systemName);
      });

      socket.on("admin_alert", (data: any) => {
        showCheckMissed(data.systemName, data.claimedBy);
      });

      // Org-wide announcement from an admin. Ephemeral — backend doesn't
      // persist or replay these, so only users connected at send time see it.
      socket.on("org_toast", ({ message, fromName }: { message: string; fromName: string }) => {
        showCustomToast(`Announcement from ${fromName}`, message, "info");
      });

      return () => {
        socket.off("systems");
        socket.off("system_online");
        socket.off("system_offline");
        socket.off("system_change");
        socket.off("ping_update");
        socket.off("ss_progress");
        socket.off("ss_failed");
        socket.off("system_removed");
        socket.off("claim_failed");
        socket.off("system_action_failed");
        socket.off("check_reminder");
        socket.off("admin_alert");
        socket.off("org_toast");
      };
    }
  }, [auth.isAuthenticated, auth.user?.sub, socket]);

  return (
    <SystemsContext.Provider value={{ systems, setSystems }}>
      {children}
    </SystemsContext.Provider>
  );
};

// Custom hook to use the systems context
export const useSystems = () => {
  const context = useContext(SystemsContext);
  if (!context) {
    throw new Error("useSystems must be used within a SystemsProvider");
  }
  return context;
};
